import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { getProductImageUrl } from 'Utils/getProductImageUrl'
import { useSelector } from 'react-redux'

const CustomerDisplayContext = createContext()

export const useCustomerDisplay = () => {
  const context = useContext(CustomerDisplayContext)
  if (!context) {
    throw new Error(
      'useCustomerDisplay must be used within CustomerDisplayProvider'
    )
  }
  return context
}

export const CustomerDisplayProvider = ({ children }) => {
  const history = useHistory()
  const location = useLocation()
  const { CompanyId, BranchOfficeId } = useSelector(state => state.company)

  // Data states
  const [listProducts, setListProducts] = useState([])
  const [isLoadingProductSearch, setIsLoadingProductSearch] = useState(true)
  const [productsCart, setProductsCart] = useState([])
  const [productDetail, setProductDetail] = useState(null)
  const [quoteData, setQuoteData] = useState({})
  const [isLoadingDetailQuote, setIsLoadingDetailQuote] = useState(false)
  const [subtotal, setSubtotal] = useState(0)
  const [itbms, setItbms] = useState(0)
  const [total, setTotal] = useState(0)
  const [company, setCompany] = useState(CompanyId)
  const [branchOffice, setBranchOffice] = useState(BranchOfficeId)
  const [isChangeCompany, setIsChangeCompany] = useState(false)
  const [showDiscount, setShowDiscount] = useState(true)
  const [priceChange, setPriceChange] = useState(false)
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false)
  const [isSleepMode, setIsSleepMode] = useState(false)

  // Track last active route for sleep mode recovery
  const [lastActiveRoute, setLastActiveRoute] = useState(
    '/customer_display/cart'
  )
  const channelRef = useRef(null)
  const isClosingProductDetailRef = useRef(false)
  const lastActivitySentRef = useRef(0)
  const prevProductsCartLengthRef = useRef(0)

  // Initialize BroadcastChannel immediately
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel('customer_display')
  }

  // Track last active route (non-idle, non-product-detail routes)
  useEffect(() => {
    if (
      location.pathname !== '/customer_display' &&
      !location.pathname.includes('/product/')
    ) {
      setLastActiveRoute(location.pathname)
    }
  }, [location])

  // Navigate to cart when a product is added from search or when cart goes from empty to non-empty
  useEffect(() => {
    const prevLength = prevProductsCartLengthRef.current
    prevProductsCartLengthRef.current = productsCart.length

    const productWasAdded = productsCart.length > prevLength
    const onSearchPage = location.pathname === '/customer_display/search'

    if (
      productWasAdded &&
      (prevLength === 0 || onSearchPage) &&
      !isProductDetailOpen
    ) {
      history.push('/customer_display/cart')
    }
  }, [productsCart, isProductDetailOpen, history, location.pathname])

  // Navigate to background when cart becomes empty
  useEffect(() => {
    if (
      location.pathname === '/customer_display/cart' &&
      productsCart.length === 0 &&
      !isProductDetailOpen
    ) {
      history.push('/customer_display')
    }
  }, [productsCart, location.pathname, history, isProductDetailOpen])

  // Handle product detail closing to prevent cart data flicker
  useEffect(() => {
    if (!isProductDetailOpen && isClosingProductDetailRef.current) {
      // Reset the flag after a short delay
      const timeout = setTimeout(() => {
        isClosingProductDetailRef.current = false
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [isProductDetailOpen])

  // Notify main app of user activity to reset inactivity timer
  useEffect(() => {
    const notifyActivity = () => {
      const now = Date.now()
      // Throttle: only send message once per second
      if (now - lastActivitySentRef.current > 1000) {
        if (channelRef.current) {
          channelRef.current.postMessage({ userActivity: true })
          lastActivitySentRef.current = now
        }
      }
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, notifyActivity))

    return () => {
      events.forEach(e => window.removeEventListener(e, notifyActivity))
    }
  }, [])

  // BroadcastChannel message handling
  useEffect(() => {
    const channel = channelRef.current

    channel.onmessage = event => {
      // Handle product list updates
      if (event.data.listProducts) {
        setListProducts(event.data.listProducts)
      }

      // Handle shopping cart updates (skip if closing product detail to avoid flicker)
      // Array.isArray guards against isAddedProduct messages (productCart is a single object there)
      // event.data.isProductDetail === undefined guards against Product.jsx handleClose sending
      // stale productCart bundled with isProductDetail:false, which would restore old cart data
      if (
        event.data.isProductDetail === undefined &&
        Array.isArray(event.data.productCart) &&
        !isClosingProductDetailRef.current &&
        !event.data.listProducts
      ) {
        setProductsCart(event.data.productCart)
      }

      // Handle quote data updates
      if (event.data.quoteData) {
        setQuoteData(event.data.quoteData)
      }

      // Handle quote loading state
      if (event.data.isLoadingDetailQuote !== undefined) {
        setIsLoadingDetailQuote(event.data.isLoadingDetailQuote)
      }

      // Handle discount visibility
      if (event.data.showDiscount !== undefined) {
        setShowDiscount(event.data.showDiscount)
      }

      // Handle price changes
      if (event.data.priceChange) {
        setPriceChange(event.data.priceChange)
      }

      // Handle loading states
      if (event.data.isLoading !== undefined) {
        setIsLoadingProductSearch(event.data.isLoading)
      }

      // Handle financial totals
      if (event.data.subtotal !== undefined) {
        setSubtotal(event.data.subtotal)
      }
      if (event.data.itbms !== undefined) {
        setItbms(event.data.itbms)
      }
      if (event.data.total !== undefined) {
        setTotal(event.data.total)
      }

      // Handle company switching
      if (event.data.company) {
        setCompany(event.data.company)
        setIsChangeCompany(event.data.isChangeCompany)

        // Update branchOffice if it comes in the same message
        if (event.data.branchOffice) {
          setBranchOffice(event.data.branchOffice)
        }
      }

      // Handle branch office switching (when sent independently)
      if (event.data.branchOffice && !event.data.company) {
        setBranchOffice(event.data.branchOffice)
      }

      // NAVIGATION LOGIC - Route based on message type

      // Navigate to quote detail
      if (event.data.isDetailQuote) {
        history.push('/customer_display/quote')
      }

      // Navigate to product search
      if (event.data.isSearchProducts) {
        history.push('/customer_display/search')
      }

      // Show product detail modal (guard against ghost clicks right after closing)
      if (event.data.isProductDetail && !isClosingProductDetailRef.current) {
        setProductDetail(event.data.productDetail)
        setIsProductDetailOpen(true)
      }

      // Close product detail modal when main app closes it
      if (event.data.isProductDetail === false) {
        handleSetIsProductDetailOpen(false)
      }

      // Handle sleep mode (show overlay instead of navigating)
      if (event.data.isSleepMode !== undefined) {
        setIsSleepMode(event.data.isSleepMode)
      }

      // Close window if requested
      if (event.data.windowClose) {
        window.close()
      }
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.close()
        channelRef.current = null
      }
    }
  }, [history])

  // Wrapper function to handle product detail closing
  const handleSetIsProductDetailOpen = value => {
    if (value === false && isProductDetailOpen) {
      // Mark that we're closing the product detail
      isClosingProductDetailRef.current = true
    }
    setIsProductDetailOpen(value)
  }

  // Computed values
  const isValidQuoteData = quoteData && Object.keys(quoteData).length > 0

  const setTableData = products => {
    if (!products) return []
    return products?.map(product => ({
      referencia: product?.Sku,
      descripción: product?.ProductName,
      cantidad: product?.Quantity,
      precio_unitario:
        !showDiscount || product?.Price > product?.BasePrice
          ? product?.BasePrice === null
            ? 0
            : product?.Price
          : product?.BasePrice,
      descuento: product?.DiscountAmount,
      precio_total: product?.Price * product?.Quantity,
      image: getProductImageUrl(product),
    }))
  }

  const productsData = isValidQuoteData ? setTableData(quoteData?.Lines) : []

  const value = {
    // Data states
    listProducts,
    isLoadingProductSearch,
    productsCart,
    productDetail,
    quoteData,
    productsData,
    isLoadingDetailQuote,
    subtotal,
    itbms,
    total,
    company,
    branchOffice,
    isChangeCompany,
    showDiscount,
    priceChange,
    lastActiveRoute,
    isProductDetailOpen,
    isSleepMode,

    // Setters (if needed by child components)
    setListProducts,
    setIsLoadingProductSearch,
    setProductsCart,
    setProductDetail,
    setQuoteData,
    setSubtotal,
    setItbms,
    setTotal,
    setCompany,
    setBranchOffice,
    setIsChangeCompany,
    setShowDiscount,
    setPriceChange,
    setIsProductDetailOpen: handleSetIsProductDetailOpen,

    // Broadcast channel reference
    channel: channelRef.current,
  }

  return (
    <CustomerDisplayContext.Provider value={value}>
      {children}
    </CustomerDisplayContext.Provider>
  )
}
